import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { StatisticsAggregateInterval } from '../enums/StatisticsAggregateInterval'
import SendProjectInvite from '../types/SendProjectInvite'
import {
  ActionRequest,
  DateRange,
  Emission,
  EmissionQuery,
  EnergyConsumption,
  EnergyConsumptionQuery,
  EnergyCost,
  EnergyCostQuery,
  Project,
  ProjectRequestParams,
  ProjectPhase,
  StatisticsAggregate,
  PowerSupply,
  Device,
  ProjectBaseInput,
  RuuviTag
} from '../types'
import ProjectInput from '../types/ProjectInput'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import createAPIClient from '../services/apiClient'
import ProjectQuery from '../types/ProjectQuery'
import { RequestProjectAccessInput } from '../types/RequestProjectAccessInput'
import { RequestAccessProject } from '../types/RequestAccessProject'
import ProjectTimeline from '../types/ProjectTimeline'

const api = createAPIClient('/v2')

export async function getProjects(
  query: ProjectQuery = {},
  config?: Omit<AxiosRequestConfig, 'params'>
): Promise<Project[]> {
  const response = await api.get(`/projects`, {
    ...config,
    params: query
  })

  return response.data
}

export async function getProjectsByLocation(
  query: RequestProjectAccessInput
): Promise<RequestAccessProject[]> {
  const response = await api.get(`/projects/by-location`, {
    params: query
  })
  return response.data
}

async function getActiveProjects(): Promise<Project[]> {
  const response = await api.get(`/projects`, {
    params: {
      showActive: true
    }
  })

  return response.data
}

export async function getProjectsByCompanyId(
  companyId: number | string | undefined,
  showDeleted?: boolean
): Promise<Project[]> {
  const response = await api.get(`/projects`, {
    params: {
      companyId: companyId,
      showDeleted: showDeleted ?? false
    }
  })

  return response.data
}

export async function getProject(
  id: number | string | undefined,
  config?: AxiosRequestConfig
): Promise<Project> {
  const params = { ...(config?.params ?? {}) }

  if (params.includeArchived === undefined) {
    params.includeArchived = true
  }

  const response = await api.get(`/projects/${id}`, {
    ...config,
    params
  })

  return response.data
}

async function createProject(input: ProjectInput): Promise<Project> {
  const response = await api.post('/projects', input)

  return response.data
}

export async function createProjectBase(input: ProjectBaseInput): Promise<Project> {
  const response = await api.post('/projects/base', input)

  return response.data
}

export async function updateProject(
  id: number,
  input: ProjectInput,
  config?: AxiosRequestConfig
): Promise<Project> {
  const response = await api.put(`/projects/${id}`, input, config)

  return response.data
}

export async function archiveProject(id: number | string): Promise<Project> {
  const response = await api.patch(`/projects/${id}/archive`)

  return response.data
}

export async function activateProject(id: number | string): Promise<Project> {
  const response = await api.patch(`/projects/${id}/activate`)

  return response.data
}

export async function updateProjectTimeline(
  id: number,
  data: ProjectTimeline,
  config?: AxiosRequestConfig
): Promise<Project> {
  const response = await api.put(`/projects/${id}/timeline`, data, config)

  return response.data
}

export async function deleteProject(
  id: number | string,
  deletePermanently?: boolean
): Promise<void> {
  await api.delete(`/projects/${id}`, {
    params: {
      permanently: deletePermanently ?? false
    }
  })
}

export async function recoverProject(id: number): Promise<Project> {
  const response = await api.patch(`/projects/${id}/recover`)

  return response.data
}

// FIXME: Move to meService.
async function getMyProjects(): Promise<AxiosResponse> {
  const response = await api.get(`/me/projects`)

  return response.data
}

// FIXME: Move to meService.
async function updateActiveMembership(id: string): Promise<AxiosResponse> {
  const response = await api.put(`/me`, {
    activeProjectMembershipId: id
  })

  return response
}

export async function sendProjectInvite(id: number, input: SendProjectInvite) {
  return await api.post(`/projects/${id}/invite`, input)
}

export async function getActionRequestsByProject(
  id: string | number,
  params: ProjectRequestParams
): Promise<ActionRequest[]> {
  const response = await api.get(`projects/${id}/action-requests`, {
    params: params
  })

  return response.data
}

export async function getLevelsByBlueprints(
  id: number,
  config?: AxiosRequestConfig
): Promise<number[]> {
  const response = await api.get(`/projects/${id}/blueprints/levels`, config)

  return response.data
}

/**
 * Get the statistics aggregates of a project.
 */
export async function getStatisticsAggregatesOfProject(
  id: number,
  query: DateRange,
  config?: AxiosRequestConfig
): Promise<StatisticsAggregate[]> {
  const response = await api.get(`/projects/${id}/statistics-aggregates`, {
    ...config,
    params: {
      interval: StatisticsAggregateInterval.ONE_HOUR,
      ...query
    }
  })

  return response.data
}

/**
 * Get the energy consumptions of a project.
 */
export async function getEnergyConsumptionsOfProject(
  id: number,
  query: EnergyConsumptionQuery,
  config?: AxiosRequestConfig
): Promise<EnergyConsumption[]> {
  const response = await api.get(`/projects/${id}/energy-consumptions`, {
    ...config,
    params: query
  })

  return response.data
}

/**
 * Get the energy costs of a project.
 */
export async function getEnergyCostsOfProject(
  id: number,
  query: EnergyCostQuery,
  config?: AxiosRequestConfig
): Promise<EnergyCost[]> {
  const response = await api.get(`/projects/${id}/energy-costs`, {
    ...config,
    params: query
  })

  return response.data
}

/**
 * Get the energy emissions of a project.
 */
export async function getEnergyEmissionsOfProject(
  id: number,
  query: EmissionQuery,
  config?: AxiosRequestConfig
): Promise<Emission[]> {
  const response = await api.get(`/projects/${id}/energy-emissions`, {
    ...config,
    params: query
  })

  return response.data
}

export async function getProjectAlertRules(id: number): Promise<AlertGroup[]> {
  const response = await api.get(`/projects/alertrules/${id}`)

  return response.data
}

export async function updateProjectAlertRules(
  id: number,
  input: AlertGroup[]
): Promise<AlertGroup[]> {
  const response = await api.put(`/projects/alertrules/${id}`, input)

  return response.data
}

export async function getAlertTemplates(): Promise<AlertTemplate[]> {
  const response = await api.get(`/projects/alertrules/templates`)

  return response.data
}

export async function getProjectPhases(id: number): Promise<ProjectPhase[]> {
  const response = await api.get(`/projects/${id}/project-phases`)

  return response.data
}

export async function getPowerSuppliesByProjectId(
  projectId: number,
  config?: AxiosRequestConfig
): Promise<PowerSupply[]> {
  const response = await api.get(`/projects/${projectId}/powersupplies`, config)

  return response.data
}

export async function getDevicesOfProject(
  id: number,
  config?: AxiosRequestConfig
): Promise<Device[]> {
  const response = await api.get(`/projects/${id}/devices`, config)

  return response.data
}

export async function getJoinLink(id: number): Promise<string> {
  const response = await api.get(`/projects/${id}/join-link`)

  return response.data
}

export async function joinProjectByToken(token: string): Promise<void> {
  const response = await api.post(`/projects/join/${token}`)

  await response.data
}

/**
 * Get the devices by project that have notified an alert.
 */
export async function getAlertNotifiedDevicesByProjectService(
  id: number,
  config?: AxiosRequestConfig
) {
  const response: AxiosResponse<Device[]> = await api.get(
    `/projects/${id}/alert-notified-devices`,
    config
  )

  return response.data
}

export const getAlertingRuuviTagsByProjectService = async (
  id: number,
  config?: AxiosRequestConfig
): Promise<RuuviTag[]> => {
  const response: AxiosResponse<RuuviTag[]> = await api.get(
    `/projects/${id}/ruuvi-tags/alerting`,
    config
  )

  return response.data
}

export default {
  getProjects,
  getActiveProjects,
  getProjectsByCompanyId,
  getMyProjects,
  getProject,
  createProject,
  createProjectBase,
  updateProject,
  deleteProject,
  updateActiveMembership,
  getActionRequestsByProject,
  getLevelsByBlueprints,
  getProjectAlertRules,
  getProjectPhases,
  getPowerSuppliesByProjectId,
  getJoinLink,
  updateProjectTimeline
}
